/*
 * The hand signature on the quote page (owner, 2026-10-30, like Jobber): the client signs in a box with a finger or a
 * mouse before a quote is approved. Each stroke ends by handing back the box as a small PNG data link; Clear starts over.
 */
import { useEffect, useRef, useState } from "react";

const W = 600;
const H = 180;

export function SignaturePad({ onChange, label = "Sign here" }: { onChange: (png: string | null) => void; label?: string }) {
  const box = useRef<HTMLCanvasElement>(null);
  const last = useRef<{ x: number; y: number } | null>(null);
  const [inked, setInked] = useState(false);

  useEffect(() => {
    const ctx = box.current?.getContext("2d");
    if (!ctx) return;
    ctx.lineWidth = 3;
    ctx.lineCap = "round";
    ctx.lineJoin = "round";
    ctx.strokeStyle = "#111111";
  }, []);

  // Pointer spot in the canvas's own pixels (the box is drawn smaller or larger than W x H on screen).
  const at = (e: React.PointerEvent<HTMLCanvasElement>) => {
    const r = e.currentTarget.getBoundingClientRect();
    return { x: ((e.clientX - r.left) * W) / r.width, y: ((e.clientY - r.top) * H) / r.height };
  };
  const line = (to: { x: number; y: number }) => {
    const ctx = box.current?.getContext("2d");
    const from = last.current;
    if (!ctx || !from) return;
    ctx.beginPath();
    ctx.moveTo(from.x, from.y);
    ctx.lineTo(to.x === from.x && to.y === from.y ? to.x + 0.1 : to.x, to.y);
    ctx.stroke();
    last.current = to;
  };
  const done = () => {
    if (!last.current) return;
    last.current = null;
    setInked(true);
    onChange(box.current?.toDataURL("image/png") ?? null);
  };
  const clear = () => {
    box.current?.getContext("2d")?.clearRect(0, 0, W, H);
    setInked(false);
    onChange(null);
  };

  return (
    <div className="sign-pad">
      <div className="sign-pad-top">
        <span className="public-label">{label}</span>
        {inked ? (
          <button type="button" className="link" onClick={clear}>
            Clear
          </button>
        ) : null}
      </div>
      <canvas
        ref={box}
        width={W}
        height={H}
        className="sign-pad-box"
        role="img"
        aria-label={inked ? "Your signature" : "Signature box. Sign with your finger or mouse."}
        onPointerDown={(e) => {
          e.currentTarget.setPointerCapture(e.pointerId);
          last.current = at(e);
          line(at(e));
        }}
        onPointerMove={(e) => line(at(e))}
        onPointerUp={done}
        onPointerCancel={done}
      />
      {!inked ? <span className="muted small">Use your finger or mouse.</span> : null}
    </div>
  );
}
